import type { VariableOption } from "../../s25-dropdown/single-select/s25.bpe.vars.dropdown.component";
import { Doc } from "./s25.document.const";
import { Item } from "../../../pojo/Item";
import { GenericDropdownUtil } from "../../s25-dropdown/generic/generic.dropdown.util";
import type { DropDownItem } from "../../../pojo/DropDownItem";
import { Document, DocumentService } from "../../../services/document.service";
import { SpreadSheetService } from "../../s25-spreadsheet/spreadsheet.service";
import type { SheetEntry } from "../../s25-spreadsheet/types/sheet.js.types.expanded";
import type { Report } from "../../../pojo/Report";
import { ReportBuilderUtil } from "./s25.report.builder.util";
import { S25Util } from "../../../util/s25-util";

export namespace DocumentUtil {
    export function isEventRole(option: VariableOption): boolean {
        return option.type === "roleDropdown" && option.data?.type === Item.Ids.Event;
    }

    export function isEventAttribute(option: VariableOption): boolean {
        return option.type === "searchCriteriaItem" && option.data?.criterion === "eventCustomAttributes";
    }

    export function typesToDropdown(types: Doc.Type[]): DropDownItem[] {
        const items: DropDownItem[] = types.map((type) => ({ ...type, val: type.id, txt: type.label }));
        GenericDropdownUtil.addGroups(items, "category");
        return items;
    }

    export async function exportDocument(id: number, name: string) {
        const documents: Document[] = [];

        let toFetch: number[] = [id];
        while (!!toFetch.length) {
            const fetched = await Promise.all(toFetch.map((id) => DocumentService.getDocument(id)));
            documents.push(...fetched);
            toFetch = fetched
                .flatMap((doc) => {
                    if (!canReferenceDocuments(doc)) return undefined;
                    return ReportBuilderUtil.getSubreportIds(
                        doc.body_text,
                        doc.document_type as Doc.ReportBuilderTypeId,
                    );
                })
                .filter(S25Util.isDefined);
        }

        // When removing duplicates keep last appearance
        const seen: Set<number> = new Set();
        for (let i = documents.length - 1; i >= 0; i--) {
            if (seen.has(documents[i].document_id)) {
                documents.splice(i, 1);
            }
            seen.add(documents[i].document_id);
        }

        await SpreadSheetService.downloadExcel(documents as unknown as SheetEntry[], `Exported Document: ${name}.xlsx`);
    }

    export function canReferenceDocuments(document: Pick<Document, "document_type">): boolean {
        return Doc.type[document.document_type].interface === "reportBuilder";
    }

    export function hasReport(document: Pick<Document, "document_scope">): boolean {
        return document.document_scope !== "subreport";
    }

    export function setReportApplyDateRange(
        document: Pick<Document, "document_type" | "apply_date_range">,
        report: Report.Meta,
    ) {
        const param = report.parameter?.find((param) => param.parameter_name === "Apply Date Range");
        if (!!param) {
            param.validate = document.apply_date_range;
            param.status = "mod";
        }
    }
}
